import { useCallback } from 'react'
import { getNestedOrFloatingElementById } from '@fs/zion-sls'

/**
 * Custom hook for handling LinkNamesPanel save operations
 * Provides a reusable handler that updates linkedPersonIds in form fields when users link/unlink persons
 *
 * Supports two update strategies:
 * 1. resetField - for regular form fields (EssentialInformation: names, SEX_CODE, RACE_OR_COLOR, AGE)
 * 2. updateField - for field arrays (AdditionalFacts)
 *
 * @param {Object} params
 * @param {Function} params.getValues - React Hook Form's getValues function to read current field data
 * @param {Function} params.resetField - React Hook Form's resetField function for regular fields (optional)
 * @param {Function} params.updateField - Field array update function for array fields (optional)
 * @param {Array} params.images - SLS images array for looking up person elements
 * @param {String} params.currentImageArk - Current image ARK for partition key fallback
 * @returns {Object} Object containing handleLinkNamesSave function
 *
 * @example
 * // For regular fields (names, personal details)
 * const { handleLinkNamesSave } = useLinkNamesSave({
 *   getValues,
 *   resetField,
 *   images,
 *   currentImageArk
 * })
 * <LinkNamesButton onSave={handleLinkNamesSave('SEX_CODE')} />
 *
 * @example
 * // For field arrays (additional facts)
 * const { handleLinkNamesSave } = useLinkNamesSave({
 *   getValues,
 *   updateField: (index, data) => fieldArrayMethods.update(index, data),
 *   images,
 *   currentImageArk
 * })
 * <LinkNamesButton onSave={handleLinkNamesSave(index)} />
 */
export const useLinkNamesSave = ({ getValues, resetField, updateField, images, currentImageArk }) => {
  /**
   * Creates a save handler for a specific field
   * @param {String|Number} fieldIdentifier - Field name (string) for regular fields or index (number) for field arrays
   * @returns {Function} Handler function that processes link/unlink data from LinkNamesPanel
   */
  const handleLinkNamesSave = useCallback(
    (fieldIdentifier) => (data) => {
      const { toLink, toUnlink } = data
      const isFieldArray = typeof fieldIdentifier === 'number'
      const fieldName = isFieldArray ? `additionalFacts.${fieldIdentifier}` : fieldIdentifier
      const currentFieldData = getValues(fieldName)

      if (!currentFieldData) return

      // Update linkedPersonIds based on toLink/toUnlink changes
      // linkedPersonIds is an array of objects: [{id: string, partitionKey: string}]
      let updatedLinkedPersonIds = [...(currentFieldData.linkedPersonIds || [])]

      // Remove unlinked persons
      if (toUnlink && toUnlink.length > 0) {
        updatedLinkedPersonIds = updatedLinkedPersonIds.filter((personObj) => {
          const personObjId = typeof personObj === 'string' ? personObj : personObj.id
          return !toUnlink.includes(personObjId)
        })
      }

      // Add newly linked persons
      if (toLink && toLink.length > 0) {
        toLink.forEach((personIdToLink) => {
          const alreadyLinked = updatedLinkedPersonIds.some((personObj) => {
            const existingId = typeof personObj === 'string' ? personObj : personObj.id
            return existingId === personIdToLink
          })

          if (!alreadyLinked) {
            const personElement = getNestedOrFloatingElementById(images, personIdToLink)
            if (personElement) {
              updatedLinkedPersonIds.push({
                id: personIdToLink,
                partitionKey: personElement.partitionKey || currentImageArk,
              })
            }
          }
        })
      }

      // Update the form field based on the update strategy
      const newFieldData = {
        ...currentFieldData,
        linkedPersonIds: updatedLinkedPersonIds,
        shared: currentFieldData?.shared || updatedLinkedPersonIds.length > 1,
      }

      if (isFieldArray && updateField) {
        // Field array strategy: use update function
        updateField(fieldIdentifier, newFieldData)
      } else if (resetField) {
        // Regular field strategy: use resetField
        resetField(fieldName, {
          defaultValue: newFieldData,
          keepDirty: true, // Keep form dirty since this is an unsaved change
          keepErrors: false,
          keepTouched: true,
        })
      }
    },
    [getValues, resetField, updateField, images, currentImageArk]
  )

  return { handleLinkNamesSave }
}
